import React, { useCallback, useState } from "react";
import { useField } from "formik";
import {
  FormControl,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CancelIcon from "@mui/icons-material/Cancel";

import { outlinedInput } from "../../utils/classes";
import FieldLabel from "../FieldLabel";
import HelperText from "../HelperText";

interface IAddressInput {
  senderAddress: string;
  sendToSameAccountHelper?: boolean;
  placeholder?: string;
  name: string;
  disabled: boolean;
  error: string | undefined;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void;
  value: string | undefined;
  label: string;
}

const AddressInput: React.FC<IAddressInput> = ({
  senderAddress,
  placeholder,
  name,
  disabled,
  sendToSameAccountHelper = false,
  error,
  onChange,
  setFieldValue,
  value,
  label,
}: IAddressInput) => {
  const [field, meta, helpers] = useField(name);
  const [stored, setStored] = useState<string | undefined>();
  const theme = useTheme();

  const toggleReceiver = useCallback(() => {
    if (stored === undefined) {
      setStored(field.value);
      helpers.setValue(senderAddress);
    } else {
      helpers.setValue(stored);
      setStored(undefined);
    }
  }, [helpers, field, senderAddress, stored, setStored]);

  return (
    <>
      <FieldLabel label={label} disabled={disabled} />
      <FormControl fullWidth>
        <OutlinedInput
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={onChange}
          sx={outlinedInput}
          error={!!error}
          name={name}
          endAdornment={
            error && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setFieldValue(name, "")}
                  edge="end"
                >
                  <CancelIcon color="error" />
                </IconButton>
              </InputAdornment>
            )
          }
        />
        {error && <HelperText error={error} />}
      </FormControl>
      {sendToSameAccountHelper && ( // TODO: Fix it, in old version the implementation doent work
        <FormControlLabel
          disabled={disabled} // it should be disabled on the first screen because the user did not connect a wallet
          control={<Checkbox />}
          label="I want to send funds to my address"
          value={stored !== undefined}
          onChange={() => toggleReceiver()}
          color={theme.palette.secondary.main}
          sx={{
            marginTop: 7,
            "& .MuiSvgIcon-root": {
              fontSize: 28,
              color: theme.palette.secondary.light,
            },
            "& .MuiFormControlLabel-label": {
              color: theme.palette.secondary.main,
              fontWeight: 700,
              "&.Mui-disabled": {
                color: theme.palette.secondary.light,
              },
            },
          }}
        />
      )}
    </>
  );
};

export default AddressInput;
